import { useEffect } from 'react'
import type { RefObject } from 'react'

/**
 * Dismisses a floating layer on a pointer press outside every supplied element.
 * Uses `pointerdown` so the layer closes before a click lands on whatever is beneath.
 */
export const useOnClickOutside = (
  refs: readonly RefObject<HTMLElement | null>[],
  handler: (event: PointerEvent) => void,
  enabled = true,
): void => {
  useEffect(() => {
    if (!enabled) return

    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target
      if (!(target instanceof Node)) return
      for (const ref of refs) {
        const node = ref.current
        if (node && node.contains(target)) return
      }
      handler(event)
    }

    document.addEventListener('pointerdown', onPointerDown, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
    }
  }, [refs, handler, enabled])
}
